/**
 * Liste déroulante étiquetée.
 *
 * Sert partout où le contrat impose une **liste fermée** (rareté, élément, type d'effet,
 * statistique…) : l'administrateur ne peut pas saisir une valeur hors schéma, et le serveur
 * refuserait de toute façon (`400 VALIDATION_FAILED`).
 */
import type { SelectHTMLAttributes } from 'react';

export interface OptionSelection {
  readonly valeur: string;
  readonly libelle: string;
}

export interface ProprietesSelection extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  readonly id: string;
  readonly etiquette: string;
  readonly options: readonly OptionSelection[];
  readonly aide?: string;
  readonly erreur?: string | null;
}

export function Selection({
  id,
  etiquette,
  options,
  aide,
  erreur,
  className,
  ...reste
}: ProprietesSelection) {
  const idAide = aide === undefined ? null : `${id}-aide`;
  const idErreur = erreur === null || erreur === undefined ? null : `${id}-erreur`;
  const decrit = [idAide, idErreur].filter((valeur): valeur is string => valeur !== null);
  const classes = ['champ', className].filter(Boolean).join(' ');
  return (
    <div className={classes}>
      <label className="champ__etiquette" htmlFor={id}>
        {etiquette}
      </label>
      <select
        {...reste}
        id={id}
        className="champ__saisie"
        aria-invalid={idErreur === null ? undefined : true}
        aria-describedby={decrit.length === 0 ? undefined : decrit.join(' ')}
      >
        {options.map((option) => (
          <option key={option.valeur} value={option.valeur}>
            {option.libelle}
          </option>
        ))}
      </select>
      {idAide === null ? null : (
        <p className="champ__aide" id={idAide}>
          {aide}
        </p>
      )}
      {idErreur === null ? null : (
        <p className="champ__erreur" id={idErreur} role="alert">
          {erreur}
        </p>
      )}
    </div>
  );
}
